import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {ACCOUNT_QUERY_KEY_MAP} from '../api/account/constant';
import {getAccountCash} from '../api/account/getAccountCash';
import {getAccountUser} from '../api/account/getAccountUser';
import {shouldFetchAccount} from '../api/account/shouldFetchAccount';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import {PagewebError} from '../errors/PagewebError.class';
import {createUser} from '../graphql/factory/User';
import {createUserCash} from '../graphql/factory/UserCash';
import type {
  UserAndCashQuery,
  UserAndCashQueryVariables,
} from '../graphql/generated/types';
import logger from '../log/logger';

/**
 * Account 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 * 2개의 API (user, cash)를 Promise.all로 병렬 호출
 */
export const accountFactory = {
  /**
   * 사용자 / 캐시 정보 조회
   *
   * Origin: useUserAndCashQuery
   *
   * ⚠️ Resolver 로직:
   * - authInput 또는 로그인 정보가 없으면 API 호출 생략
   * - 사용자 정보 조회 실패 시 에러 throw
   * - 캐시 정보 조회 실패 시 빈 객체 반환 + 로그 남김
   * - requestContext에서 age, authenticatedAt 가져옴
   */
  userAndCash: (
    variables?: UserAndCashQueryVariables,
    options?: Omit<
      UseQueryOptions<UserAndCashQuery, PagewebError, UserAndCashQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: ACCOUNT_QUERY_KEY_MAP.userAndCash(variables),
      queryFn: async () => {
        // authInput 또는 로그인 정보가 없으면 API 호출 생략
        if (!shouldFetchAccount(variables)) {
          return {
            userAndCash: {
              user: undefined,
              cash: undefined,
            },
          };
        }

        const userInfo = requestContext.requestInfo.user;

        // 2개 API 병렬 호출
        const [userResponse, cashResponse] = await Promise.all([
          // User API
          (async () => {
            const response = await getAccountUser(variables, fetcherOptions);

            if (!response) {
              throw new Error('사용자 정보 가져오기 실패');
            }
            return createUser({
              userVo: response.profile,
              age:
                userInfo?.age !== undefined ? Number(userInfo?.age) : undefined,
              authenticatedAt: userInfo?.authenticatedAt
                ? userInfo?.authenticatedAt
                : undefined,
            });
          })(),
          // Cash API (실패해도 빈 객체 반환)
          (async () => {
            try {
              const response = await getAccountCash(fetcherOptions);
              if (!response) {
                logger.err(
                  '로그인 정보 중 캐시 정보 가져오기 실패: 데이터 없음'
                );
                return {};
              }
              return createUserCash(response);
            } catch (error) {
              logger.err(
                `로그인 정보 요청 중 캐시 가져오기 실패: ${
                  (error as Error)?.message ?? ''
                }`
              );
              return {};
            }
          })(),
        ]);

        return {
          userAndCash: {
            user: userResponse,
            cash: cashResponse,
          },
        };
      },
      ...options,
    }),
} as const;
